import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Pricing from "@/pages/Pricing";
import Showcase from "@/pages/Showcase";
import ShowcaseTrack from "@/pages/ShowcaseTrack";
import ArtistPage from "@/pages/ArtistPage";
import ArtistsPage from "@/pages/ArtistsPage";
import PlaylistPage from "@/pages/PlaylistPage";
import SharePage from "@/pages/SharePage";
import PortalPage from "@/pages/PortalPage";
import TikTok from "@/pages/TikTok";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/DashboardLayout";
import Studio from "@/pages/Studio";
import Library from "@/pages/Library";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Gaming from "@/pages/Gaming";
import Voice from "@/pages/Voice";
import Synth from "@/pages/Synth";
import Themes from "@/pages/Themes";
import Narrative from "@/pages/Narrative";
import Promo from "@/pages/Promo";
import Social from "@/pages/Social";
import Billing from "@/pages/Billing";
import ApiKeys from "@/pages/ApiKeys";
import Docs from "@/pages/Docs";
import PaymentResult from "@/pages/PaymentResult";
import BrandLoader from "@/components/BrandLoader";
import ScenePreview from "@/pages/ScenePreview";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <BrandLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/showcase" element={<Showcase />} />
      <Route path="/showcase/:id" element={<ShowcaseTrack />} />
      <Route path="/artist/:slug" element={<ArtistPage />} />
      <Route path="/artists" element={<ArtistsPage />} />
      <Route path="/playlist/:id" element={<PlaylistPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route path="/portal" element={<PortalPage />} />
      <Route path="/portal/:token" element={<PortalPage />} />
      <Route path="/tiktok" element={<TikTok />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/share/scene/:token" element={<ScenePreview />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Studio />} />
        <Route path="library" element={<Library />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectId" element={<ProjectDetail />} />
        <Route path="gaming" element={<Gaming />} />
        <Route path="voice" element={<Voice />} />
        <Route path="synth" element={<Synth />} />
        <Route path="themes" element={<Themes />} />
        <Route path="narrative" element={<Narrative />} />
        <Route path="promo" element={<Promo />} />
        <Route path="social" element={<Social />} />
        <Route path="billing" element={<Billing />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="docs" element={<Docs />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
